/**
 * Browser-local equivalents of the Lit Actions.
 *
 * All WASM runs directly in-browser — no PKP, no Lit network required.
 * The seed is stored as plain base64 in localStorage (dev/test only).
 * The proving service is expected to run on localhost:3001.
 */

import { base64 } from "@scure/base";
import { entropyToMnemonic, mnemonicToSeedSync } from "@scure/bip39";
import { wordlist as english } from "@scure/bip39/wordlists/english";

// WASM bytecodes — bundled as base64 JSON by the lit-actions build
import * as keyDerivationBytecode from "../../../lit-actions/src/generated/wasm/zcash-key-derivation-core-wasm.json";
import * as keyScanningBytecode from "../../../lit-actions/src/generated/wasm/zcash-key-derivation-scanning-wasm.json";
import * as transactionBytecode from "../../../lit-actions/src/generated/wasm/zcash-transaction-wasm.json";
import * as transparentTxBytecode from "../../../lit-actions/src/generated/wasm/zcash-transparent-tx-wasm.json";

// WASM JS bindings
import {
  derive_transparent_address,
  derive_unified_address,
  derive_diversified_unified_address,
  derive_orchard_spending_key,
  initSync as initKeyDerivationSync,
} from "../../../lit-actions/wasm/zcash-key-derivation-core/zcash_key_derivation_core_wasm";

import {
  derive_full_viewing_key as scan_derive_fvk,
  scan_for_notes,
  self_test_scan,
  initSync as initScanningSync,
} from "../../../lit-actions/wasm/zcash-key-derivation-scanning/zcash_key_derivation_scanning_wasm";

import {
  create_shielding_tx,
  create_unshielding_tx,
  initSync as initTransactionSync,
} from "../../../lit-actions/wasm/zcash-transaction/zcash_transaction_wasm";

import {
  create_transparent_tx,
  initSync as initTransparentTxSync,
} from "../../../lit-actions/wasm/zcash-transparent-tx/zcash_transparent_tx_wasm";

// ---------------------------------------------------------------------------
// WASM init helpers
// ---------------------------------------------------------------------------

function initKeyDerivationWasm() {
  const { wasmBufferBase64 } = keyDerivationBytecode as { wasmBufferBase64: string };
  try {
    initKeyDerivationSync(new WebAssembly.Module(base64.decode(wasmBufferBase64) as BufferSource));
  } catch (e) {
    if (!(e instanceof Error) || !e.message.includes("already")) throw e;
  }
}

let scanningWasmReady = false;
function initScanningWasm() {
  const { wasmBufferBase64 } = keyScanningBytecode as { wasmBufferBase64: string };
  try {
    initScanningSync(new WebAssembly.Module(base64.decode(wasmBufferBase64) as BufferSource));
  } catch (e) {
    if (!(e instanceof Error) || !e.message.includes("already")) throw e;
  }
  if (!scanningWasmReady) {
    scanningWasmReady = true;
    try {
      const selfTest = JSON.parse(self_test_scan());
      const result = selfTest.notes_found === 1 ? "PASSED" : `FAILED(notes=${selfTest.notes_found})`;
      console.log("[local-wasm] selfTest:", result);
    } catch (e) {
      console.warn("[local-wasm] selfTest ERROR:", e instanceof Error ? e.message : e);
    }
  }
}

function initTransactionWasm() {
  const { wasmBufferBase64 } = transactionBytecode as { wasmBufferBase64: string };
  try {
    initTransactionSync(new WebAssembly.Module(base64.decode(wasmBufferBase64) as BufferSource));
  } catch (e) {
    if (!(e instanceof Error) || !e.message.includes("already")) throw e;
  }
}

function initTransparentTxWasm() {
  const { wasmBufferBase64 } = transparentTxBytecode as { wasmBufferBase64: string };
  try {
    initTransparentTxSync(new WebAssembly.Module(base64.decode(wasmBufferBase64) as BufferSource));
  } catch (e) {
    if (!(e instanceof Error) || !e.message.includes("already")) throw e;
  }
}

// ---------------------------------------------------------------------------
// Seed generation
// ---------------------------------------------------------------------------

export interface LocalGenerateSeedResult {
  seedB64: string;
  transparentAddress: string;
  unifiedAddress: string;
  unifiedAddressEnter: string;
  unifiedAddressExit: string;
}

/**
 * The message the user signs to deterministically derive their ZEC seed.
 * Never changes — same wallet always produces the same ZEC addresses.
 */
export const DERIVATION_MESSAGE =
  "Espectro ZEC account derivation v1\n\nSigning this message derives your private ZEC intermediary wallet. This signature never leaves your browser.";

/**
 * Derive a ZEC seed deterministically from an ETH wallet signature.
 * Same ETH wallet → same signature → same ZEC seed → same addresses.
 * Recoverable even after browser cache is cleared.
 */
export async function localDeriveZcashSeedFromSignature(
  ethSignature: string,
): Promise<LocalGenerateSeedResult> {
  // sha256(signature hex) → 32 bytes of deterministic entropy
  const sigBytes = new TextEncoder().encode(ethSignature);
  const hashBuf = await crypto.subtle.digest("SHA-256", sigBytes);
  const entropy = new Uint8Array(hashBuf);
  return _seedFromEntropy(entropy);
}

export async function localGenerateZcashSeed(): Promise<LocalGenerateSeedResult> {
  const entropy = crypto.getRandomValues(new Uint8Array(32));
  return _seedFromEntropy(entropy);
}

async function _seedFromEntropy(entropy: Uint8Array): Promise<LocalGenerateSeedResult> {
  const mnemonic = entropyToMnemonic(entropy, english);
  const seedBytes = mnemonicToSeedSync(mnemonic);
  const seedB64 = base64.encode(seedBytes);

  initKeyDerivationWasm();
  const transparentAddress = derive_transparent_address(seedBytes);
  const unifiedAddress = derive_unified_address(seedBytes);
  const unifiedAddressEnter = derive_diversified_unified_address(seedBytes, 0);
  const unifiedAddressExit = derive_diversified_unified_address(seedBytes, 1);

  return { seedB64, transparentAddress, unifiedAddress, unifiedAddressEnter, unifiedAddressExit };
}

// ---------------------------------------------------------------------------
// Key derivation
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Shielding (t-addr -> z-addr)
// ---------------------------------------------------------------------------

export async function localCreateShieldingTx(params: {
  seedB64: string;
  utxos: string;
  amount: string;
  provingServiceUrl?: string;
}): Promise<{ txid: string; raw_tx: string }> {
  const seedBytes = base64.decode(params.seedB64);

  initKeyDerivationWasm();
  initTransactionWasm();

  const spendingKey = derive_orchard_spending_key(seedBytes);

  const paramsJson = JSON.stringify({
    utxos: JSON.parse(params.utxos),
    amount: params.amount,
    spending_key: base64.encode(spendingKey),
    proving_service_url: params.provingServiceUrl ?? "http://localhost:3001",
  });

  const resultJson = await create_shielding_tx(paramsJson, seedBytes);
  const result = JSON.parse(resultJson);
  return { txid: result.txid, raw_tx: result.raw_tx };
}

// ---------------------------------------------------------------------------
// Unshielding (z-addr -> t-addr)
// ---------------------------------------------------------------------------

export async function localCreateUnshieldingTx(params: {
  seedB64: string;
  notes: string;
  amount: string;
  destinationTAddr: string;
  provingServiceUrl?: string;
  anchor?: string;
  merklePaths?: string;
}): Promise<{ txid: string; raw_tx: string }> {
  const seedBytes = base64.decode(params.seedB64);

  initKeyDerivationWasm();
  initTransactionWasm();

  const spendingKey = derive_orchard_spending_key(seedBytes);

  const paramsJson = JSON.stringify({
    notes: JSON.parse(params.notes),
    amount: params.amount,
    destination_t_addr: params.destinationTAddr,
    spending_key: base64.encode(spendingKey),
    proving_service_url: params.provingServiceUrl ?? "http://localhost:3001",
    anchor: params.anchor,
    merkle_paths: params.merklePaths ? JSON.parse(params.merklePaths) : undefined,
  });

  const resultJson = await create_unshielding_tx(paramsJson, seedBytes);
  const result = JSON.parse(resultJson);
  return { txid: result.txid, raw_tx: result.raw_tx };
}

// ---------------------------------------------------------------------------
// Transparent send (t-addr -> t-addr)
// ---------------------------------------------------------------------------

export function localCreateTransparentTx(params: {
  seedB64: string;
  utxos: string;
  amount: string;
  destinationAddress: string;
}): { txid: string; raw_tx: string } {
  const seedBytes = base64.decode(params.seedB64);

  initKeyDerivationWasm();
  initTransparentTxWasm();

  const paramsJson = JSON.stringify({
    utxos: JSON.parse(params.utxos),
    amount: params.amount,
    destination_address: params.destinationAddress,
  });

  const resultJson = create_transparent_tx(paramsJson, seedBytes);
  const result = JSON.parse(resultJson);
  return { txid: result.txid, raw_tx: result.raw_tx };
}

// ---------------------------------------------------------------------------
// Shielded balance scan
// ---------------------------------------------------------------------------

export interface LocalBalanceResult {
  balance: string;
  noteCount: number;
  latestBlockHeight: number;
  notes?: Array<{
    value: number;
    note_commitment: string;
    nullifier: string;
    recipient: string;
    rho: string;
    rseed: string;
    position?: number;
    auth_path?: string[];
  }>;
  anchor?: string;
  seenNullifiers?: string[];
}

export function localGetShieldedBalance(params: {
  seedB64: string;
  compactBlocks: string;
  frontier?: string;
}): LocalBalanceResult {
  const seedBytes = base64.decode(params.seedB64);

  initScanningWasm();

  const fvk = scan_derive_fvk(seedBytes);
  const fvkBytes = new TextEncoder().encode(fvk);
  const blockData = base64.decode(params.compactBlocks);

  const resultJson = scan_for_notes(blockData, fvkBytes, params.frontier ?? undefined);
  const result = JSON.parse(resultJson);

  let totalBalance = BigInt(0);
  for (const note of result.notes) {
    totalBalance += BigInt(note.value);
  }

  return {
    balance: totalBalance.toString(),
    noteCount: result.notes.length,
    latestBlockHeight: result.latest_block_height,
    notes: result.notes,
    anchor: result.anchor ?? undefined,
    seenNullifiers: result.seen_nullifiers ?? undefined,
  };
}
